import { CategoryDTO, CategoryDTOSchema, CategoryEntity } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { DocumentSettingsSkeleton } from '@/components/settings/settings-form-skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/toaster';
import { usePermissions } from '@/hooks/use-permissions';
import { documentCategoryKeys, getDocumentCategoriesQueryOptions } from '@/queries/documents';
import { ApiService } from '@/services/api-v2';
import { useUserStore } from '@/stores/user.store';

import DocumentCategories from './document-categories';

const DocumentSettingsFormSchema = z.object({
  categories: z.array(CategoryDTOSchema),
});

type DocumentSettingsFormData = z.infer<typeof DocumentSettingsFormSchema>;

function DocumentSettingsForm({ defaultValues }: { defaultValues: DocumentSettingsFormData }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { canEditWorkspaceSettings } = usePermissions();

  const {
    watch,
    setValue,
    handleSubmit,
    reset,
    formState: { isDirty, isSubmitting },
  } = useForm<DocumentSettingsFormData>({
    resolver: zodResolver(DocumentSettingsFormSchema),
    defaultValues,
  });

  const formData = watch();

  const { mutateAsync: updateDocumentCategories } = useMutation({
    mutationFn: async ({ categories }: { categories: CategoryDTO[] }) => await ApiService.UpdateCategories({ categories, entity: CategoryEntity.Document }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: documentCategoryKeys.all,
      });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  function handleCategoriesChange(categories: CategoryDTO[]) {
    setValue('categories', categories, { shouldDirty: true });
  }

  async function onSubmit(data: DocumentSettingsFormData) {
    try {
      if (data.categories && data.categories.length > 0) {
        await updateDocumentCategories({
          categories: data.categories.map((category, index) => ({
            ...category,
            order: index,
          })),
        });
      }

      toast.success('Document categories updated successfully');
      reset(data);
    } catch {
      // Do nothing, errors are handled by the mutation
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <DocumentCategories onCategoriesChange={handleCategoriesChange} documentCategories={formData.categories} disabled={!canEditWorkspaceSettings} />
      {canEditWorkspaceSettings && (
        <Button type="submit" variant="success" className="w-fit" disabled={!isDirty || isSubmitting}>
          Save changes
        </Button>
      )}
    </form>
  );
}

function SettingsDocumentsPage() {
  const { org } = useUserStore();

  const { data: documentCategories } = useQuery(getDocumentCategoriesQueryOptions({ orgId: org?.id }));

  return (
    <div className="flex w-full max-w-[833px] flex-col gap-4 rounded-lg bg-white p-6 shadow-lg">
      <ScrollArea viewportClassName="pr-2">
        <div className="flex flex-col gap-4">
          <h1 className="text-default-text text-xl font-medium">Documents</h1>
          {!documentCategories && <DocumentSettingsSkeleton />}
          {documentCategories && <DocumentSettingsForm defaultValues={{ categories: documentCategories }} />}
        </div>
      </ScrollArea>
    </div>
  );
}

export default SettingsDocumentsPage;
